import React, { useState, memo, useRef, useCallback, useEffect } from 'react'
import { createPortal } from 'react-dom'
import VideoPlayer from './VideoPlayer'
import ImageComponent from '../../common/ImageComponent'
import imageCacheService from '../../../services/ImageCacheService'

// Simplified VideoThumbnail - only uses API thumbnail
const VideoThumbnail = memo(({ url, thumbnail, index, onImageClick }) => {
  return (
    <div
      style={{ width: "100%", height: "100%", position: "relative" }}
      onClick={(e) => {
        e.preventDefault()
        e.stopPropagation()
        onImageClick(index)
      }}
    >
      {/* Display thumbnail from API or default placeholder */}
      <ImageComponent
        src={thumbnail || "/icons/No-postimage-icon.jpeg"}
        alt="Video thumbnail"
        lazy={true}
        fallback="/icons/No-postimage-icon.jpeg"
        style={{
          width: "100%",
          height: "100%",
          objectFit: "cover",
          display: "block"
        }}
      />

      {/* Video play overlay */}
      <div
        style={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          background: "rgba(0, 0, 0, 0.7)",
          borderRadius: "50%",
          width: "60px",
          height: "60px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          pointerEvents: "none"
        }}
      >
        <svg width="24" height="24" viewBox="0 0 24 24" fill="white">
          <polygon points="5,3 19,12 5,21" />
        </svg>
      </div>
    </div>
  )
})

const MultiMediaDisplay = memo(({ media, onRemoveItem, style, slideHeight = 400 }) => {
  const [selectedImageIndex, setSelectedImageIndex] = useState(null)
  const [currentIndex, setCurrentIndex] = useState(0)
  const [isDragging, setIsDragging] = useState(false)
  const [startX, setStartX] = useState(0)
  const [dragOffset, setDragOffset] = useState(0)
  const [isSwiping, setIsSwiping] = useState(false)
  
  if (!media || media.length === 0) return null

  // Minimum swipe distance (in px)
  const minSwipeDistance = 50

  const onDragStart = (clientX) => {
    setIsDragging(true)
    setStartX(clientX)
    setDragOffset(0)
    setIsSwiping(false)
  }

  const onDragMove = (clientX) => {
    if (!isDragging) return
    let offset = clientX - startX
    
    // Add resistance at boundaries
    if (currentIndex === 0 && offset > 0) {
      offset = Math.pow(offset, 0.7) // Resist dragging right on first item
    } else if (currentIndex === media.length - 1 && offset < 0) {
      offset = -Math.pow(Math.abs(offset), 0.7) // Resist dragging left on last item
    }

    setDragOffset(offset)
    if (Math.abs(offset) > 10) {
      setIsSwiping(true)
    }
  }

  const onDragEnd = () => {
    if (!isDragging) return
    
    if (Math.abs(dragOffset) > minSwipeDistance) {
      if (dragOffset < 0 && currentIndex < media.length - 1) {
        handleNext()
      } else if (dragOffset > 0 && currentIndex > 0) {
        handlePrev()
      }
    }
    
    setIsDragging(false)
    setDragOffset(0)
    // Delay resetting isSwiping to prevent accidental click on release
    setTimeout(() => setIsSwiping(false), 50)
  }

  // Touch handlers
  const onTouchStart = (e) => onDragStart(e.targetTouches[0].clientX)
  const onTouchMove = (e) => onDragMove(e.targetTouches[0].clientX)
  const onTouchEnd = () => onDragEnd()

  // Mouse handlers
  const onMouseDown = (e) => {
    // Only track left click
    if (e.button !== 0) return
    
    // Prevent default browser drag-and-drop ghost image
    if (e.target.tagName === 'IMG' || e.target.tagName === 'VIDEO') {
      e.preventDefault()
    }
    
    onDragStart(e.clientX)
  }
  const onMouseMove = (e) => onDragMove(e.clientX)
  const onMouseUp = () => onDragEnd()
  const onMouseLeave = () => {
    if (isDragging) onDragEnd()
  }

  const handleNext = useCallback(() => {
    setCurrentIndex((prev) => (prev === media.length - 1 ? prev : prev + 1))
  }, [media.length])

  const handlePrev = useCallback(() => {
    setCurrentIndex((prev) => (prev === 0 ? 0 : prev - 1))
  }, [media.length])

  const handleImageClick = (index) => {
    setSelectedImageIndex(index)
  }

  const closeModal = useCallback(() => {
    setSelectedImageIndex(null)
  }, [])

  // Handle scroll locking safely
  useEffect(() => {
    if (selectedImageIndex !== null && typeof document !== 'undefined') {
      // Check if body is already locked by a parent modal (like PostDetailModal)
      const isAlreadyLocked = document.body.style.position === 'fixed' || document.body.style.overflow === 'hidden';
      
      if (!isAlreadyLocked) {
        const originalOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        
        return () => {
          document.body.style.overflow = originalOverflow;
        };
      }
    }
  }, [selectedImageIndex]);

  // Handle keyboard events (Escape to close)
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        // Stop propagation to prevent parent modals from closing
        e.stopPropagation()
        closeModal()
      }
    }

    if (selectedImageIndex !== null) {
      document.addEventListener('keydown', handleKeyDown, true)
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown, true)
    }
  }, [selectedImageIndex, closeModal])

  const handleDownload = useCallback(async (e) => {
    e.stopPropagation()
    if (selectedImageIndex === null) return

    const currentMedia = media[selectedImageIndex]
    const url = typeof currentMedia === 'string' ? currentMedia : currentMedia.url
    const fileName = typeof currentMedia === 'string'
      ? url.split('/').pop() || 'download'
      : currentMedia.file_name || url.split('/').pop() || 'download'

    try {
      // Fetch the file
      const response = await fetch(url)
      const blob = await response.blob()

      // Create a temporary URL and trigger download
      const blobUrl = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = blobUrl
      link.download = fileName
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(blobUrl)
    } catch (error) {
      console.error('Failed to download media:', error)
      // Fallback: open in new tab
      window.open(url, '_blank')
    }
  }, [selectedImageIndex, media])


  return (
    <>
      <div 
        className="multi-media-slider-container" 
        style={{ 
          position: "relative", 
          overflow: "hidden", 
          width: "100%", 
          borderRadius: "8px",
          background: "#fff",
          userSelect: "none",
          WebkitUserSelect: "none",
          touchAction: "pan-y",
          ...style 
        }}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        onMouseDown={onMouseDown}
        onMouseMove={onMouseMove}
        onMouseUp={onMouseUp}
        onMouseLeave={onMouseLeave}
      >
        <div 
          className="multi-media-slider-track"
          style={{
            display: "flex",
            transition: isDragging ? "none" : "transform 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
            transform: `translateX(calc(-${currentIndex * 100}% + ${dragOffset}px))`,
            width: "100%",
            height: "100%",
            minHeight: `${slideHeight}px`
          }}
        >
          {media.map((item, index) => {
            const url = typeof item === 'string' ? item : item.url
            const type = typeof item === 'string' ?
              (item.includes('.mp4') || item.includes('.mov') || item.includes('.webm') || item.includes('.m3u8') ? 'video' :
                item.includes('.pdf') || item.includes('.doc') || item.includes('.docx') ||
                  item.includes('.txt') || item.includes('.rtf') ? 'document' : 'image') :
              (item.type?.includes('video') || item.file_type?.includes('video') ? 'video' :
                item.type?.includes('document') || item.file_type?.includes('document') || item.type?.includes('pdf') || item.file_type?.includes('pdf') ? 'document' : 'image')

            return (
              <div 
                key={index} 
                className="multi-media-slide"
                style={{
                  flex: "0 0 100%",
                  width: "100%",
                  height: "100%",
                  position: "relative",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer"
                }}
                onClick={(e) => {
                  if (isSwiping) {
                    e.preventDefault()
                    e.stopPropagation()
                    return
                  }
                  handleImageClick(index)
                }}
              >
                {/* Remove button for edit mode */}
                {onRemoveItem && (
                  <button
                    onClick={(e) => {
                      e.preventDefault()
                      e.stopPropagation()
                      onRemoveItem(index)
                    }}
                    style={{
                      position: "absolute",
                      top: "12px",
                      right: "12px",
                      background: "rgba(0, 0, 0, 0.7)",
                      color: "white",
                      border: "none",
                      borderRadius: "50%",
                      width: "28px",
                      height: "28px",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      zIndex: 10,
                      fontSize: "14px",
                    }}
                  >
                    ×
                  </button>
                )}

                {type === "video" ? (
                  <div style={{ width: "100%", height: "100%", minHeight: "400px" }}>
                    <VideoPlayer
                      src={url}
                      thumbnail={item.thumbnail}
                      resolutions={item.resolutions}
                      autoPlay={false}
                    />
                  </div>
                ) : type === "document" ? (
                  <div
                    style={{
                      width: "100%",
                      height: "100%",
                      minHeight: "400px",
                      background: "#f3f4f6",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      padding: "20px"
                    }}
                  >
                    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ color: "#6b7280" }}>
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                      <polyline points="14,2 14,8 20,8" />
                      <line x1="16" y1="13" x2="8" y2="13" />
                      <line x1="16" y1="17" x2="8" y2="17" />
                      <polyline points="10,9 9,9 8,9" />
                    </svg>
                    <span style={{ fontSize: "14px", color: "#6b7280", marginTop: "12px", textAlign: "center", fontWeight: "500" }}>
                      {url.split('/').pop() || 'Document'}
                    </span>
                  </div>
                ) : (
                    <ImageComponent
                      src={url || "/icons/No-postimage-icon.jpeg"}
                      alt={`Media ${index + 1}`}
                      lazy={true}
                      fallback="/icons/No-postimage-icon.jpeg"
                      draggable={false}
                      style={{
                        width: "100%",
                        height: "100%",
                        maxHeight: "600px",
                        objectFit: "contain",
                        display: "block",
                        pointerEvents: "none",
                        userSelect: "none",
                      }}
                    />
                )}
              </div>
            )
          })}
        </div>

        {/* Navigation Arrows (Desktop only or optional) */}
        {media.length > 1 && (
          <>
            {currentIndex > 0 && (
              <button
                onClick={(e) => { e.stopPropagation(); handlePrev(); }}
                style={{
                  position: "absolute",
                  left: "10px",
                  top: "50%",
                  transform: "translateY(-50%)",
                  background: "rgba(0, 0, 0, 0.3)",
                  color: "white",
                  border: "none",
                  borderRadius: "50%",
                  width: "30px",
                  height: "30px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  zIndex: 5,
                  fontSize: "18px"
                }}
              >
                ‹
              </button>
            )}
            {currentIndex < media.length - 1 && (
              <button
                onClick={(e) => { e.stopPropagation(); handleNext(); }}
                style={{
                  position: "absolute",
                  right: "10px",
                  top: "50%",
                  transform: "translateY(-50%)",
                  background: "rgba(0, 0, 0, 0.3)",
                  color: "white",
                  border: "none",
                  borderRadius: "50%",
                  width: "30px",
                  height: "30px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  zIndex: 5,
                  fontSize: "18px"
                }}
              >
                ›
              </button>
            )}
          </>
        )}

        {/* Mini Circles (Dots) */}
        {media.length > 1 && (
          <div 
            className="slider-dots"
            style={{
              position: "absolute",
              bottom: "15px",
              left: "50%",
              transform: "translateX(-50%)",
              display: "flex",
              gap: "8px",
              zIndex: 5
            }}
          >
            {media.map((_, index) => (
              <div
                key={index}
                onClick={(e) => { e.stopPropagation(); setCurrentIndex(index); }}
                style={{
                  width: "8px",
                  height: "8px",
                  borderRadius: "50%",
                  background: currentIndex === index ? "#8548A8" : "rgba(133, 72, 168, 0.3)",
                  transition: "all 0.2s ease",
                  cursor: "pointer",
                  border: "1px solid rgba(0, 0, 0, 0.05)"
                }}
              />
            ))}
          </div>
        )}
      </div>

      {/* Media Modal */}
      {selectedImageIndex !== null && createPortal(
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(0, 0, 0, 0.9)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 10000,
            cursor: "pointer",
            backgroundColor: "black",
            overflow: "hidden",

          }}
          onClick={closeModal}
        >
          <div
            style={{
              position: "relative",
              width: "100vw",
              height: "100vh",
              display: "flex",
              marginBottom: 0,
              // alignItems: "center",
              justifyContent: "center"
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {(() => {
              const currentMedia = media[selectedImageIndex]
              const url = typeof currentMedia === 'string' ? currentMedia : currentMedia.url
              const type = typeof currentMedia === 'string' ?
                (currentMedia.includes('.mp4') || currentMedia.includes('.mov') || currentMedia.includes('.webm') || currentMedia.includes('.m3u8') ? 'video' :
                  currentMedia.includes('.pdf') || currentMedia.includes('.doc') || currentMedia.includes('.docx') ||
                    currentMedia.includes('.txt') || currentMedia.includes('.rtf') ? 'document' : 'image') :
                (currentMedia.type?.includes('video') || currentMedia.file_type?.includes('video') ? 'video' :
                  currentMedia.type?.includes('document') || currentMedia.file_type?.includes('document') || currentMedia.type?.includes('pdf') || currentMedia.file_type?.includes('pdf') ? 'document' : 'image')

              // Handle documents
              if (type === 'document') {
                return (
                  <div
                    style={{
                      background: "white",
                      borderRadius: "8px",
                      padding: "40px",
                      maxWidth: "90vw",
                      maxHeight: "90vh",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      textAlign: "center"
                    }}
                  >
                    <div
                      style={{
                        width: "120px",
                        height: "120px",
                        background: "#f3f4f6",
                        borderRadius: "12px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        marginBottom: "20px"
                      }}
                    >
                      <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                        <polyline points="14,2 14,8 20,8" />
                        <line x1="16" y1="13" x2="8" y2="13" />
                        <line x1="16" y1="17" x2="8" y2="17" />
                        <polyline points="10,9 9,9 8,9" />
                      </svg>
                    </div>
                    <h3 style={{ margin: "0 0 12px 0", fontSize: "18px", fontWeight: "600", color: "#1f2937" }}>
                      {url.split('/').pop() || 'Document'}
                    </h3>
                    <p style={{ margin: "0 0 24px 0", fontSize: "14px", color: "#6b7280" }}>
                      Click below to download or view this document
                    </p>
                    <div style={{ display: "flex", gap: "12px" }}>
                      <a
                        href={url}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          background: "#8548A8",
                          color: "white",
                          padding: "12px 24px",
                          borderRadius: "6px",
                          textDecoration: "none",
                          fontSize: "14px",
                          fontWeight: "500",
                          display: "flex",
                          alignItems: "center",
                          gap: "8px"
                        }}
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                          <polyline points="7,10 12,15 17,10" />
                          <line x1="12" y1="15" x2="12" y2="3" />
                        </svg>
                        Download
                      </a>
                      <a
                        href={url}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          background: "transparent",
                          color: "#8548A8",
                          padding: "12px 24px",
                          borderRadius: "6px",
                          textDecoration: "none",
                          fontSize: "14px",
                          fontWeight: "500",
                          border: "2px solid #8548A8",
                          display: "flex",
                          alignItems: "center",
                          gap: "8px"
                        }}
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                          <polyline points="15,3 21,3 21,9" />
                          <line x1="10" y1="14" x2="21" y2="3" />
                        </svg>
                        Open
                      </a>
                    </div>
                  </div>
                )
              }

              // Handle videos
              if (type === 'video') {
                const thumbnail = typeof currentMedia === 'string' ? null : currentMedia.thumbnail
                const resolutions = typeof currentMedia === 'string' ? null : currentMedia.resolutions

                return (
                  <div style={{
                    width: "80vw",
                    height: "80vh",
                    maxWidth: "1200px",
                    maxHeight: "800px",
                    borderRadius: "8px",
                    marginBottom: "20px",
                    overflow: "hidden",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    backgroundColor: "#000"
                  }}>
                    <VideoPlayer
                      src={url}
                      thumbnail={thumbnail}
                      resolutions={resolutions}
                      onClose={closeModal}
                      autoPlay={true}
                    />
                  </div>
                )
              }

              // Handle images (default)
              return (
                <div
                  style={{
                    background: "transparent",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: "100vw",
                    height: "100vh",
                  }}
                >
                  <ImageComponent
                    src={url || "/icons/No-postimage-icon.jpeg"}
                    alt={`Media ${selectedImageIndex + 1}`}
                    lazy={false}
                    fallback="/icons/No-postimage-icon.jpeg"
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "contain",
                      borderRadius: 0
                    }}
                  />
                </div>
              )
            })()}

            {/* Thumbnail Preview Bar */}
            <div
              style={{
                position: "fixed",
                bottom: "0px",
                left: "50%",
                transform: "translateX(-50%)",
                display: "flex",
                gap: "8px",
                padding: "10px",
                background: "rgba(0, 0, 0, 0.8)",
                borderRadius: "8px",
                maxWidth: "90vw",
                overflowX: "auto",
                zIndex: 10001,
              }}
            >
              {media.map((item, index) => {

                const url = typeof item === 'string' ? item : item.url
                const type = typeof item === 'string' ?
                  (item.includes('.mp4') || item.includes('.mov') || item.includes('.webm') || item.includes('.m3u8') ? 'video' : 'image') :
                  (item.type?.includes('video') || item.file_type?.includes('video') ? 'video' : 'image')

                return (
                  <div
                    key={index}
                    onClick={(e) => {
                      e.stopPropagation()
                      setSelectedImageIndex(index)
                    }}
                    style={{
                      width: "60px",
                      height: "60px",
                      borderRadius: "4px",
                      overflow: "hidden",
                      cursor: "pointer",
                      border: selectedImageIndex === index ? "3px solid #8548A8" : "2px solid transparent",
                      opacity: selectedImageIndex === index ? 1 : 0.7,
                      transition: "all 0.2s ease"
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.opacity = "1"
                    }}
                    onMouseLeave={(e) => {
                      if (selectedImageIndex !== index) {
                        e.target.style.opacity = "0.7"
                      }
                    }}
                  >
                    {type === "video" ? (
                      <div style={{
                        width: "100%",
                        height: "100%",
                        background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        position: "relative"
                      }}>
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="white">
                          <path d="M8 5v14l11-7z" />
                        </svg>
                      </div>
                    ) : type === "document" ? (
                      <div
                        style={{
                          width: "100%",
                          height: "100%",
                          background: "#f3f4f6",
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          justifyContent: "center",
                          padding: "4px"
                        }}
                      >
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ color: "#6b7280" }}>
                          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                          <polyline points="14,2 14,8 20,8" />
                          <line x1="16" y1="13" x2="8" y2="13" />
                          <line x1="16" y1="17" x2="8" y2="17" />
                          <polyline points="10,9 9,9 8,9" />
                        </svg>
                        <span style={{ fontSize: "8px", color: "#6b7280", marginTop: "2px", textAlign: "center" }}>
                          {url.split('/').pop()?.split('.')[1]?.toUpperCase() || 'DOC'}
                        </span>
                      </div>
                    ) : (
                      <ImageComponent
                        src={url || "/icons/No-postimage-icon.jpeg"}
                        alt={`Thumbnail ${index + 1}`}
                        lazy={false}
                        fallback="/icons/No-postimage-icon.jpeg"
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "contain"
                        }}
                      />
                    )}
                  </div>
                )
              })}
            </div>

            {/* Close button */}
            <button
              onClick={closeModal}
              style={{
                position: "fixed",
                top: "20px",
                right: "20px",
                background: "rgba(0, 0, 0, 0.7)",
                color: "white",
                border: "none",
                borderRadius: "50%",
                width: "40px",
                height: "40px",
                cursor: "pointer",
                fontSize: "20px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                zIndex: 10001,
              }}
            >
              ×
            </button>

            {/* Download button - only show for images and videos (documents have their own download) */}
            {(() => {
              const currentMedia = media[selectedImageIndex]
              const type = typeof currentMedia === 'string' ?
                (currentMedia.includes('.mp4') || currentMedia.includes('.mov') || currentMedia.includes('.webm') || currentMedia.includes('.m3u8') ? 'video' :
                  currentMedia.includes('.pdf') || currentMedia.includes('.doc') || currentMedia.includes('.docx') ||
                    currentMedia.includes('.txt') || currentMedia.includes('.rtf') ? 'document' : 'image') :
                (currentMedia.type?.includes('video') || currentMedia.file_type?.includes('video') ? 'video' :
                  currentMedia.type?.includes('document') || currentMedia.file_type?.includes('document') || currentMedia.type?.includes('pdf') || currentMedia.file_type?.includes('pdf') ? 'document' : 'image')

              if (type === 'document') return null // Documents have their own download button

              return (
                <button
                  onClick={handleDownload}
                  style={{
                    position: "fixed",
                    top: "20px",
                    right: "70px",
                    background: "rgba(0, 0, 0, 0.7)",
                    color: "white",
                    border: "none",
                    borderRadius: "50%",
                    width: "40px",
                    height: "40px",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    zIndex: 10001,
                    transition: "background 0.2s ease",
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.background = "rgba(0, 0, 0, 0.9)"
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.background = "rgba(0, 0, 0, 0.7)"
                  }}
                  title="Download"
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                    <polyline points="7,10 12,15 17,10" />
                    <line x1="12" y1="15" x2="12" y2="3" />
                  </svg>
                </button>
              )
            })()}

            {/* Navigation buttons */}
            {media.length > 1 && (
              <>
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    setSelectedImageIndex(prev =>
                      prev === 0 ? media.length - 1 : prev - 1
                    )
                  }}
                  style={{
                    position: "fixed",
                    left: "20px",
                    top: "50%",
                    transform: "translateY(-50%)",
                    background: "rgba(0, 0, 0, 0.7)",
                    color: "white",
                    border: "none",
                    borderRadius: "50%",
                    width: "50px",
                    height: "50px",
                    cursor: "pointer",
                    fontSize: "24px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    zIndex: 10001,
                  }}
                >
                  ‹
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    setSelectedImageIndex(prev =>
                      prev === media.length - 1 ? 0 : prev + 1
                    )
                  }}
                  style={{
                    position: "fixed",
                    right: "20px",
                    top: "50%",
                    transform: "translateY(-50%)",
                    background: "rgba(0, 0, 0, 0.7)",
                    color: "white",
                    border: "none",
                    borderRadius: "50%",
                    width: "50px",
                    height: "50px",
                    cursor: "pointer",
                    fontSize: "24px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    zIndex: 10001,
                  }}
                >
                  ›
                </button>
              </>
            )}
          </div>
        </div>, document.body)
      }
    </>
  )
}, (prevProps, nextProps) => {
  // Custom comparison function to prevent unnecessary re-renders
  const mediaChanged = JSON.stringify(prevProps.media) !== JSON.stringify(nextProps.media)
  const onRemoveItemChanged = prevProps.onRemoveItem !== nextProps.onRemoveItem
  const styleChanged = JSON.stringify(prevProps.style) !== JSON.stringify(nextProps.style)
  // Only re-render if props actually changed
  return !mediaChanged && !onRemoveItemChanged && !styleChanged
})

export default MultiMediaDisplay
